/**
 * Pure query-shape helpers for the Algolia-backed movement-manufacturer
 * CATALOGUE.
 *
 * Sibling to `brandListQuery.ts` and `calibreListQuery.ts`. Kept out of
 * `algolia.ts` so the parameter building and the hit → entry mapping can be
 * unit-tested without pulling in the Algolia client — and the `import.meta.env`
 * credential read that happens at its module scope.
 *
 * There is deliberately no picker/listing split here, unlike brands and
 * calibres. Every manufacturer surface in the admin — the pickers, the
 * `id → name` maps, and now the Movement Manufacturers listing itself — holds
 * the WHOLE catalogue and filters it locally: no surface issues a query per
 * keystroke, and at ~180 records the whole index comes back in a single request
 * (`nbPages: 1`). One projection serves them all; a narrower "picker" variant
 * would save a few KB once per modal open and cost a second code path.
 *
 * The listing's search box, type filter and Brand-include filter are all
 * client-side over that in-memory array for the same reason — a server-side
 * facet would be a round trip to narrow a list already in the browser.
 */

import { Timestamp } from 'firebase/firestore';
import type { BrandStatus, MovementManufacturer } from '../types';

/**
 * Attributes every manufacturer consumer needs.
 *
 * That is now the whole indexed surface bar three things nothing here reads:
 * `manufacturerIdAncestors` (backs an RN descendant-tree query),
 * `parentManufacturerName` (the listing resolves a parent's label through the
 * catalogue it already holds in memory, so the denormalized copy would only be a
 * second source of truth for the same string), and `hasManualBrandInclude` — see
 * {@link manufacturerHitToEntry} for why that last one is deliberately left on
 * the record rather than carried onto an entry.
 *
 * Requesting an attribute the index doesn't hold is silently dropped rather than
 * an error, so this list is only as good as the indexer it mirrors
 * (`buildManufacturerIndexRecord`, watchlock `functions/src/entityIndexes.ts`,
 * landed as #866 and extended with the five curation fields below by #867) —
 * verified against that builder, and against the live records, rather than
 * assumed.
 */
export const MANUFACTURER_CATALOGUE_ATTRIBUTES = [
  'objectID',
  'name',
  'displayName',
  'alternativeNames',
  'abbreviations',
  'country',
  'parentOrg',
  // The FK. Listings resolve the owner through this first; the legacy string
  // above is only a fallback for records that were never linked.
  'parentOrgId',
  'parentManufacturerId',
  'website',
  'logo',
  'heroImage',
  'isGeneric',
  'primaryBrandId',
  'founded',
  'status',
  'brandIdsManualInclude',
  'brandIdsDerivedFromRefs',
  'brandExclusions',
  'heritageBrandIds',
  'createdAt',
  'updatedAt',
] as const;

/**
 * Fields of `MovementManufacturer` the index does NOT carry.
 *
 * Documented rather than merely implied, because each one is a silently wrong
 * answer if a future caller assumes a catalogue entry is a whole manufacturer
 * document. What is left is exactly the edit form's own surface — long prose and
 * provenance/audit fields no listing, picker or label resolver ever renders:
 *
 * - `description` / `aiDescription` — the two long-form bodies. Deliberately
 *   unindexed: they are the bulk of a manufacturer document and nothing outside
 *   the form reads them (the index carries a `hasDescription` boolean for the
 *   one place that wants to know whether there is one).
 * - `logoSourceUrl` / `heroImageSourceUrl` — image provenance, written and read
 *   only by `resolveLogoToStorage` and the form's image fields.
 * - `needsReview` / `yearCeased` — audit-worklist and lifecycle detail edited on
 *   the form and rendered nowhere else.
 *
 * These are the whole reason `MovementManufacturerEditor` hydrates a single
 * Firestore document when the Edit modal OPENS, rather than handing the modal
 * the catalogue row behind it: the form round-trips every field it is given, so
 * a projection going in would mean six fields cleared on Save.
 *
 * The five curation fields that used to sit in this list — `status`,
 * `brandIdsManualInclude`, `brandIdsDerivedFromRefs`, `brandExclusions`,
 * `heritageBrandIds` — moved into {@link MANUFACTURER_CATALOGUE_ATTRIBUTES} once
 * watchlock #867 indexed them, which is what put the listing, the Data Health
 * overview and both brand-scoped calibre pickers on this path.
 */
export const MANUFACTURER_FIELDS_NOT_INDEXED = [
  'needsReview',
  'yearCeased',
  'description',
  'aiDescription',
  'logoSourceUrl',
  'heroImageSourceUrl',
] as const;

/**
 * The `status` values the app understands, as a runtime set.
 *
 * The index stores `status` as a plain string, so a record carrying anything
 * outside {@link BrandStatus} — a hand-edited document, a future value indexed
 * before this client knows about it — would otherwise be cast straight through
 * to `StatusBadge`, whose config lookup is an unguarded destructure and throws
 * on an unknown key. Dropping the value renders no badge, which is what an
 * absent status already does.
 */
const KNOWN_BRAND_STATUSES = new Set<string>([
  'active',
  'dormant',
  'defunct',
  'revived',
  'absorbed',
]);

/** One `watchkeeper_manufacturers` record, as the catalogue consumes it. */
export interface ManufacturerCatalogueHit {
  /** The manufacturer's Firestore doc id — `movement_manufacturers/{objectID}`. */
  objectID: string;
  name?: string;
  displayName?: string;
  alternativeNames?: string[];
  abbreviations?: string[];
  country?: string;
  parentOrg?: string;
  parentOrgId?: string;
  parentManufacturerId?: string;
  website?: string;
  logo?: string;
  heroImage?: string;
  isGeneric?: boolean;
  primaryBrandId?: string;
  founded?: number;
  /**
   * Lifecycle marker. A plain string on the record, NOT a narrowed
   * `BrandStatus` — see {@link KNOWN_BRAND_STATUSES}.
   */
  status?: string;
  brandIdsManualInclude?: string[];
  brandIdsDerivedFromRefs?: string[];
  brandExclusions?: string[];
  heritageBrandIds?: string[];
  /**
   * Indexer-derived `brandIdsManualInclude.length > 0`, kept as a facetable
   * boolean for server-side filtering. Present on the record but deliberately
   * NOT retrieved or mapped — see {@link manufacturerHitToEntry}.
   */
  hasManualBrandInclude?: boolean;
  /** Epoch milliseconds — the indexer flattens Firestore Timestamps. */
  createdAt?: number;
  /** Epoch milliseconds. */
  updatedAt?: number;
}

/**
 * A manufacturer as the catalogue serves it: the projection of
 * `MovementManufacturer` over the attributes the index actually carries.
 *
 * NARROWER than `MovementManufacturer` on purpose. A consumer that reaches for
 * one of {@link MANUFACTURER_FIELDS_NOT_INDEXED} gets a compile error here
 * instead of a silently-undefined field at runtime, which is the difference
 * between "this screen won't build" and "brand scoping quietly stopped matching
 * half its manufacturers".
 */
export type ManufacturerCatalogueEntry = Pick<
  MovementManufacturer,
  | 'id'
  | 'name'
  | 'displayName'
  | 'alternativeNames'
  | 'abbreviations'
  | 'country'
  | 'parentOrg'
  | 'parentOrgId'
  | 'parentManufacturerId'
  | 'website'
  | 'logo'
  | 'heroImage'
  | 'isGeneric'
  | 'primaryBrandId'
  | 'founded'
  | 'status'
  | 'brandIdsManualInclude'
  | 'brandIdsDerivedFromRefs'
  | 'brandExclusions'
  | 'heritageBrandIds'
  | 'createdAt'
  | 'updatedAt'
>;

/**
 * Algolia search parameters for one page of the catalogue.
 *
 * No `facets` and no `facetFilters`: every consumer holds the whole catalogue
 * locally (176 records fit in a single request), so filtering and grouping stay
 * client-side and cost no round trip.
 *
 * `attributesToHighlight: []` because no manufacturer surface renders highlight
 * markup. `analytics: false` keeps admin browsing out of the app's search
 * analytics. `cacheable: false` bypasses the SDK's in-memory response cache, so
 * a modal reopened after an edit shows the edit rather than replaying the
 * pre-edit response.
 */
export function buildManufacturerCatalogueParams(
  page: number,
  hitsPerPage: number,
): Record<string, unknown> {
  return {
    hitsPerPage,
    page,
    analytics: false,
    cacheable: false,
    attributesToRetrieve: [...MANUFACTURER_CATALOGUE_ATTRIBUTES],
    attributesToHighlight: [],
  };
}

/**
 * Map one Algolia hit onto the shape the admin's manufacturer consumers render.
 *
 * Empty strings are dropped rather than carried across. The indexer writes its
 * string attributes UNCONDITIONALLY (`asString(...)` yields `''` for a missing
 * field), so a straight copy would turn every absent `country` / `website` /
 * `parentOrg` into a present-but-empty one — and `displayName || name`, the
 * label idiom every one of these consumers uses, would then render nothing where
 * it used to fall back to the name.
 *
 * `isGeneric` is carried across verbatim including `false`, because the
 * generic/non-generic distinction in `classifyCalibreGeneric` turns on the
 * difference between an explicit `false` and an absent flag.
 *
 * The four curation arrays follow the same drop-when-empty rule as the alias
 * arrays, so an entry is indistinguishable from a Firestore document that never
 * had the field. That matters more here than for aliases: `[]` and absent read
 * the same to every consumer (`?.length ?? 0`), but carrying `[]` would make an
 * entry ALWAYS report the key as present, and the editor's optimistic patches
 * spread entries over whole documents.
 *
 * `hasManualBrandInclude` is on the record but is not mapped, on purpose. It is
 * `brandIdsManualInclude.length > 0` — derivable from a field that exists on
 * BOTH paths — whereas the boolean exists only on the Algolia path. Putting it
 * on the entry would make it silently `undefined` whenever
 * `getManufacturerCatalogue()` falls back to Firestore, which is the exact class
 * of bug this projection is typed narrowly to prevent. Consumers ask
 * `(m.brandIdsManualInclude?.length ?? 0) > 0` instead; nothing is server-side
 * filtered, so the facet has no caller.
 *
 * Reconstructing `Timestamp`s from the indexed epoch milliseconds keeps
 * consumers that call `.toDate()` / `.toMillis()` working on a hit-derived entry
 * exactly as they did on a Firestore document.
 */
export function manufacturerHitToEntry(
  hit: ManufacturerCatalogueHit,
): ManufacturerCatalogueEntry {
  const entry: ManufacturerCatalogueEntry = {
    id: hit.objectID,
    name: hit.name || hit.displayName || hit.objectID,
  };
  if (hit.displayName) entry.displayName = hit.displayName;
  if (hit.alternativeNames?.length) entry.alternativeNames = hit.alternativeNames;
  if (hit.abbreviations?.length) entry.abbreviations = hit.abbreviations;
  if (hit.country) entry.country = hit.country;
  if (hit.parentOrg) entry.parentOrg = hit.parentOrg;
  if (hit.parentOrgId) entry.parentOrgId = hit.parentOrgId;
  if (hit.parentManufacturerId) entry.parentManufacturerId = hit.parentManufacturerId;
  if (hit.website) entry.website = hit.website;
  if (hit.logo) entry.logo = hit.logo;
  if (hit.heroImage) entry.heroImage = hit.heroImage;
  if (typeof hit.isGeneric === 'boolean') entry.isGeneric = hit.isGeneric;
  if (hit.primaryBrandId) entry.primaryBrandId = hit.primaryBrandId;
  if (typeof hit.founded === 'number') entry.founded = hit.founded;
  if (hit.status && KNOWN_BRAND_STATUSES.has(hit.status)) {
    entry.status = hit.status as BrandStatus;
  }
  if (hit.brandIdsManualInclude?.length) {
    entry.brandIdsManualInclude = hit.brandIdsManualInclude;
  }
  if (hit.brandIdsDerivedFromRefs?.length) {
    entry.brandIdsDerivedFromRefs = hit.brandIdsDerivedFromRefs;
  }
  if (hit.brandExclusions?.length) entry.brandExclusions = hit.brandExclusions;
  if (hit.heritageBrandIds?.length) entry.heritageBrandIds = hit.heritageBrandIds;
  if (typeof hit.createdAt === 'number') {
    entry.createdAt = Timestamp.fromMillis(hit.createdAt);
  }
  if (typeof hit.updatedAt === 'number') {
    entry.updatedAt = Timestamp.fromMillis(hit.updatedAt);
  }
  return entry;
}

/**
 * Sort a catalogue by canonical `name`, ascending.
 *
 * Exists so the Algolia path reproduces `getMovementManufacturers()`'s
 * `orderBy('name')` exactly. Several consumers document themselves as receiving
 * a name-sorted list and render it straight into a `<select>` without sorting
 * again (`CalibreMovementTypeBackfill` says so in a comment), so an
 * unsorted Algolia response would silently scramble those dropdowns.
 *
 * Sorts on `name`, NOT on `displayName || name`: that is the field Firestore
 * ordered by. Consumers that want display order (`CustomCalibreModal`,
 * `CuratedCalibres`) re-sort on the label themselves, as they already did.
 *
 * `localeCompare` rather than `<`, so "Ébauches" files next to "Eberhard"
 * instead of after "Zenith" — the Firestore ordering is bytewise and does put it
 * last, so this is a small deliberate improvement, not a regression.
 */
export function sortManufacturersByName<T extends { name: string }>(
  entries: T[],
): T[] {
  return [...entries].sort((a, b) => a.name.localeCompare(b.name));
}

/** Columns the Manufacturers listing can be sorted by. */
export type ManufacturerSortField = 'name' | 'country' | 'parent' | 'calibres' | 'created';

/** The listing's sort: one column, one direction. */
export interface ManufacturerSort {
  field: ManufacturerSortField;
  direction: 'asc' | 'desc';
}

/** Name A→Z — the order the catalogue has always opened in. */
export const DEFAULT_MANUFACTURER_SORT: ManufacturerSort = { field: 'name', direction: 'asc' };

/**
 * The direction a column's FIRST click sorts in. Text reads A→Z; the two
 * measures open on the end people look for — most calibres, newest.
 */
export function defaultManufacturerSortDirection(
  field: ManufacturerSortField,
): 'asc' | 'desc' {
  return field === 'calibres' || field === 'created' ? 'desc' : 'asc';
}

/** A header click: reverse the active column, or switch to another at its default. */
export function nextManufacturerSort(
  current: ManufacturerSort,
  field: ManufacturerSortField,
): ManufacturerSort {
  if (current.field === field) {
    return { field, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  }
  return { field, direction: defaultManufacturerSortDirection(field) };
}

/**
 * What the two columns that are not plain fields sort by. The screen supplies
 * them so the Parent sort uses the very text the Parent cell prints (a parent
 * manufacturer's name, else the resolved parent organization) and Calibres uses
 * the count shown beside it.
 */
export interface ManufacturerSortContext<T> {
  parentText?: (entry: T) => string;
  calibreCount?: (entry: T) => number;
}

/**
 * Sort a catalogue by when each manufacturer was created.
 *
 * The NAME TIEBREAK is not cosmetic. 88 of the 188 manufacturers carry one
 * identical `createdAt` — the original bulk seed, 2026-05-26T12:51:40Z — so
 * without it oldest-first would open on an 88-row block in whatever order the
 * engine happened to return, and reshuffle on every refresh.
 *
 * A manufacturer with no `createdAt` sorts LAST in both directions. An unknown
 * date is not old and is not new; putting it at the top of "oldest first" would
 * present it as the first manufacturer ever created. Every indexed record has
 * one today, but the Firestore fallback reads raw documents, which carry no such
 * guarantee.
 *
 * Accepts anything with `toMillis()`, so it works on the Algolia path (entries
 * rebuild `Timestamp`s from epoch millis) and the Firestore one alike.
 */
export function sortManufacturersByCreated<
  T extends { name: string; createdAt?: { toMillis(): number } | null },
>(entries: T[], direction: 'asc' | 'desc'): T[] {
  const millis = (e: T): number | null => {
    const value = e.createdAt?.toMillis?.();
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  };
  return [...entries].sort((a, b) => {
    const am = millis(a);
    const bm = millis(b);
    if (am === null && bm === null) return a.name.localeCompare(b.name);
    if (am === null) return 1;
    if (bm === null) return -1;
    if (am !== bm) return direction === 'desc' ? bm - am : am - bm;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Sort by a text key. BLANK SORTS LAST IN BOTH DIRECTIONS — the same rule the
 * created sort applies to a missing date. Dozens of manufacturers have no
 * country and most have no parent; reversing the sort should not bring a wall
 * of dashes to the top. Ties break by name so the order is stable.
 */
function sortManufacturersByText<T extends { name: string }>(
  entries: T[],
  key: (entry: T) => string,
  direction: 'asc' | 'desc',
): T[] {
  const keyed = entries.map((entry) => ({ entry, text: (key(entry) ?? '').trim() }));
  keyed.sort((a, b) => {
    if (!a.text && !b.text) return a.entry.name.localeCompare(b.entry.name);
    if (!a.text) return 1;
    if (!b.text) return -1;
    const cmp = a.text.localeCompare(b.text);
    if (cmp !== 0) return direction === 'desc' ? -cmp : cmp;
    return a.entry.name.localeCompare(b.entry.name);
  });
  return keyed.map((k) => k.entry);
}

/**
 * Apply a listing sort. The default (`name`, ascending) reproduces the
 * catalogue's own order. Never mutates its input.
 */
export function sortManufacturers<
  T extends {
    name: string;
    country?: string | null;
    createdAt?: { toMillis(): number } | null;
  },
>(entries: T[], sort: ManufacturerSort, context: ManufacturerSortContext<T> = {}): T[] {
  const { field, direction } = sort;
  if (field === 'created') return sortManufacturersByCreated(entries, direction);
  if (field === 'country') return sortManufacturersByText(entries, (e) => e.country ?? '', direction);
  if (field === 'parent') {
    return sortManufacturersByText(entries, context.parentText ?? (() => ''), direction);
  }
  if (field === 'calibres') {
    const count = context.calibreCount ?? (() => 0);
    return [...entries].sort((a, b) => {
      const diff = count(a) - count(b);
      if (diff !== 0) return direction === 'desc' ? -diff : diff;
      return a.name.localeCompare(b.name);
    });
  }
  const byName = sortManufacturersByName(entries);
  return direction === 'desc' ? byName.reverse() : byName;
}
